import { useState, useEffect } from 'react';
import { Phone, MessageSquare } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { TrackedPhoneLink } from './TrackedPhoneLink';

export function StickyMobileCTA() {
  const [isVisible, setIsVisible] = useState(false);
  const [isFormInView, setIsFormInView] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Re-attach IntersectionObserver after each route change so newly-mounted
  // [data-hide-sticky-cta] sections (e.g. inline forms) on the new page are
  // observed. Without this, navigating between pages would leak stale refs.
  useEffect(() => {
    setIsFormInView(false);
    let observer: IntersectionObserver | null = null;

    // Wait one frame so the new route's DOM has rendered before querying.
    const raf = requestAnimationFrame(() => {
      const targets = document.querySelectorAll('[data-hide-sticky-cta]');
      if (targets.length === 0) return;

      observer = new IntersectionObserver(
        (entries) => {
          const anyVisible = entries.some((e) => e.isIntersecting);
          setIsFormInView(anyVisible);
        },
        { threshold: 0.15 }
      );
      targets.forEach((t) => observer!.observe(t));
    });

    return () => {
      cancelAnimationFrame(raf);
      observer?.disconnect();
    };
  }, [pathname]);

  if (!isVisible || isFormInView) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-navy-900/95 backdrop-blur-md border-t border-white/10 px-4 py-3 safe-area-pb">
      <div className="flex gap-3 max-w-lg mx-auto">
        <TrackedPhoneLink
          location="sticky_mobile_cta"
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 min-h-[48px] bg-green-600 hover:bg-green-500 text-white font-bold rounded-xl transition-colors"
        >
          <Phone className="w-5 h-5" />
          Call Now
        </TrackedPhoneLink>
        <Link
          to="/contact"
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 min-h-[48px] bg-cobalt-600 hover:bg-cobalt-500 text-white font-bold rounded-xl transition-colors"
        >
          <MessageSquare className="w-5 h-5" />
          Get Quote
        </Link>
      </div>
    </div>
  );
}
